'use client';

import React from 'react';
import { type ReactNode, type Ref, createContext, useContext } from 'react';
import { ChevronDownIcon, ChevronUpIcon } from '@milanote/roma-icons';
import type { InputProps as AriaInputProps, NumberFieldProps as AriaNumberFieldProps } from 'react-aria-components';
import {
    Button as AriaButton,
    Group as AriaGroup,
    Input as AriaInput,
    NumberField as AriaNumberField,
} from 'react-aria-components';
import { Field } from '../field';
import { cx, sortCx } from '../../../utils/cx';

const SEMANTIC_NAME_BASE = 'RomaInputNumberBase';
const SEMANTIC_NAME_FIELD = 'RomaNumberField';
const SEMANTIC_NAME = 'RomaInputNumber';

const NumberFieldContext = createContext<InputNumberContextProps>({});

type InputNumberContextProps = Pick<
    InputNumberBaseProps,
    'size' | 'isDisabled' | 'isInvalid' | 'wrapperClassName' | 'inputClassName'
>;

export interface InputNumberBaseProps extends Omit<AriaInputProps, 'size'> {
    /**
     * Input size.
     * @default "sm"
     */
    size?: 'sm' | 'md' | 'lg';
    /** Placeholder text. */
    placeholder?: string;
    /** Class name for the input. */
    inputClassName?: string;
    /** Class name for the input wrapper. */
    wrapperClassName?: string;
    /** Ref for the wrapping group element. */
    groupRef?: Ref<HTMLDivElement>;
    /** Whether the field is invalid. */
    isInvalid?: boolean;
    /** Whether the field is disabled. */
    isDisabled?: boolean;
    /** Whether the field is required. */
    isRequired?: boolean;
}

const styles = sortCx({
    stepperButton: [
        'flex flex-1 cursor-pointer items-center justify-center text-fg-quaternary outline-primary transition duration-100 ease-linear',
        'hover:bg-primary_hover hover:text-fg-quaternary_hover',
        'disabled:cursor-default disabled:bg-disabled_subtle disabled:text-fg-disabled',
    ].join(' '),
    sizes: {
        sm: {
            wrapper: 'rounded-xs',
            input: 'rounded-xs px-3 py-1.5 text-sm',
            inputWithVerticalButtons: 'pr-9',
            verticalButtons: 'w-7 rounded-r-xs',
            verticalIcon: 'size-3 stroke-3',
        },
        md: {
            wrapper: 'rounded-xs',
            input: 'rounded-xs px-3.5 py-2.5 text-md',
            inputWithVerticalButtons: 'pr-9',
            verticalButtons: 'w-7 rounded-r-xs',
            verticalIcon: 'size-3 stroke-3',
        },
        lg: {
            wrapper: 'rounded-sm',
            input: 'rounded-sm px-3.5 py-2.5 text-lg',
            inputWithVerticalButtons: 'pr-9.5',
            verticalButtons: 'w-7.5 rounded-r-sm',
            verticalIcon: 'size-3.5 stroke-[2.57px]',
        },
    },
});

export const InputNumberBase = React.forwardRef<HTMLInputElement, Omit<InputNumberBaseProps, 'label' | 'hint'>>(
    (
        {
            groupRef,
            size = 'sm',
            isInvalid,
            isDisabled,
            placeholder,
            wrapperClassName,
            inputClassName,
            // Omit this prop to avoid invalid HTML attribute warning
            isRequired: _isRequired,
            ...inputProps
        },
        ref,
    ) => {
        const context = useContext(NumberFieldContext);

        const inputSize = context?.size || size;
        const inputIsInvalid = isInvalid ?? context?.isInvalid;
        const inputIsDisabled = isDisabled ?? context?.isDisabled;

        return (
            <AriaGroup
                isDisabled={inputIsDisabled}
                isInvalid={inputIsInvalid}
                ref={groupRef}
                className={cx(
                    SEMANTIC_NAME_BASE,
                    'relative flex w-full flex-row items-stretch bg-primary transition-all duration-100 ease-linear ring-1 ring-primary ring-inset',
                    'after:pointer-events-none after:absolute after:inset-0 after:z-10 after:rounded-[inherit] after:ring-inset',
                    styles.sizes[inputSize].wrapper,
                    'focus-within:after:ring-2 focus-within:after:ring-primary_focused',

                    inputIsDisabled && 'cursor-default bg-disabled_subtle ring-disabled',
                    'group-disabled:cursor-default group-disabled:bg-disabled_subtle group-disabled:ring-disabled',

                    inputIsInvalid && 'ring-error_subtle focus-within:after:ring-error',
                    'group-invalid:ring-error_subtle group-invalid:focus-within:after:ring-error',

                    context?.wrapperClassName,
                    wrapperClassName,
                )}
            >
                <AriaInput
                    {...inputProps}
                    ref={ref}
                    placeholder={placeholder}
                    className={cx(
                        'm-0 w-full bg-transparent text-primary outline-hidden placeholder:text-placeholder',
                        'autofill:text-primary autofill:bg-transparent',
                        inputIsDisabled && 'cursor-default text-disabled text-ellipsis',
                        'group-disabled:cursor-default group-disabled:text-disabled group-disabled:text-ellipsis',
                        styles.sizes[inputSize].input,
                        styles.sizes[inputSize].inputWithVerticalButtons,
                        context?.inputClassName,
                        inputClassName,
                    )}
                />

                <div
                    className={cx(
                        'flex shrink-0 flex-col overflow-hidden border border-primary bg-primary text-fg-quaternary',
                        'group-disabled:border-disabled group-disabled:bg-disabled_subtle group-invalid:border-error_subtle group-invalid:focus-within:border-error',
                        styles.sizes[inputSize].verticalButtons,
                    )}
                >
                    <AriaButton
                        slot="increment"
                        aria-label="Increase value"
                        className={cx(
                            styles.stepperButton,
                            '-mt-px', // nudge the height of the button up just under the border
                        )}
                    >
                        <ChevronUpIcon aria-hidden="true" className={styles.sizes[inputSize].verticalIcon} />
                    </AriaButton>
                    <AriaButton
                        slot="decrement"
                        aria-label="Decrease value"
                        className={cx(
                            styles.stepperButton,
                            '-mb-px', // nudge the button down 1px so it sits flush with the bottom border
                            'border-t border-primary group-disabled:border-disabled group-invalid:border-error_subtle group-invalid:focus-within:border-error',
                        )}
                    >
                        <ChevronDownIcon aria-hidden="true" className={styles.sizes[inputSize].verticalIcon} />
                    </AriaButton>
                </div>
            </AriaGroup>
        );
    },
);

InputNumberBase.displayName = SEMANTIC_NAME_BASE;

export interface NumberFieldProps
    extends AriaNumberFieldProps,
        Pick<InputNumberBaseProps, 'size' | 'wrapperClassName' | 'inputClassName'> {}

export const NumberField = ({ className, ...props }: NumberFieldProps) => {
    return (
        <NumberFieldContext.Provider value={props}>
            <AriaNumberField
                {...props}
                data-input-wrapper
                className={(state) =>
                    cx(
                        SEMANTIC_NAME_FIELD,
                        'group flex h-max w-full flex-col items-start justify-start gap-1.5',
                        typeof className === 'function' ? className(state) : className,
                    )
                }
            />
        </NumberFieldContext.Provider>
    );
};

NumberField.displayName = SEMANTIC_NAME_FIELD;

export interface InputNumberProps
    extends AriaNumberFieldProps,
        Pick<InputNumberBaseProps, 'size' | 'wrapperClassName' | 'inputClassName'> {
    /** Placeholder text. */
    placeholder?: string;
    /** Label text for the input. */
    label?: string;
    /** Helper text displayed below the input. */
    hint?: ReactNode;
    /** Whether to hide required indicator from label. */
    hideRequiredIndicator?: boolean;
    /** Ref for the wrapping group element. */
    groupRef?: Ref<HTMLDivElement>;
}

const InputNumberRoot = React.forwardRef<HTMLInputElement, InputNumberProps>(
    (
        {
            size = 'sm',
            placeholder,
            label,
            hint,
            hideRequiredIndicator,
            className,
            groupRef,
            inputClassName,
            wrapperClassName,
            ...props
        },
        ref,
    ) => {
        return (
            <Field as={NumberField} aria-label={!label ? placeholder : undefined} {...props} className={className}>
                {({ isDisabled, isInvalid, isRequired }) => (
                    <>
                        {label && (
                            <Field.Label
                                size={size}
                                isRequired={hideRequiredIndicator ? !hideRequiredIndicator : isRequired}
                            >
                                {label}
                            </Field.Label>
                        )}

                        <InputNumberBase
                            {...{
                                ref,
                                groupRef,
                                size,
                                placeholder,
                                inputClassName,
                                wrapperClassName,
                                isDisabled,
                                isInvalid,
                            }}
                        />

                        {hint && <Field.HintText isInvalid={isInvalid}>{hint}</Field.HintText>}
                    </>
                )}
            </Field>
        );
    },
);

InputNumberRoot.displayName = SEMANTIC_NAME;

export const InputNumber = Object.assign(InputNumberRoot, {
    Base: InputNumberBase,
});
